<template>
  <div class="sf-hero">
    <div ref="glide" class="glide">
      <div class="glide__track" data-glide-el="track">
        <ul class="glide__slides sf-hero__slides">
          <slot />
        </ul>
      </div>
    </div>
    <div :style="{ display: numberOfPages <= 1 ? 'none' : 'block' }" class="sf-hero__control--left">
      <slot name="prev" v-bind="{ go: () => go('prev'), disabled: disablePrevButton }">
        <BaseButton
          class="sf-button--pure sf-hero__buttons"
          :aria-label="'previous'"
          data-testid="hero-prev-button"
          :disabled="disablePrevButton"
          @click.stop="go('prev')"
        >
          <BaseIcon icon="chevron_left" />
        </BaseButton>
      </slot>
    </div>
    <div
      :style="{ display: numberOfPages <= 1 ? 'none' : 'block' }"
      class="sf-hero__control--right"
    >
      <slot name="next" v-bind="{ go: () => go('next'), disabled: disableNextButton }">
        <BaseButton
          class="sf-button--pure sf-hero__buttons"
          :aria-label="'next'"
          data-testid="hero-next-button"
          :disabled="disableNextButton"
          @click.stop="go('next')"
        >
          <BaseIcon icon="chevron_right" />
        </BaseButton>
      </slot>
    </div>
    <div :class="{ 'display-none': numberOfPages <= 1 }" class="sf-hero__bullets">
      <slot name="bullets" v-bind="{ numberOfPages, page, go }">
        <BaseBullets
          :total="numberOfPages"
          :current="page - 1"
          data-testid="hero-bullets"
          @click="go($event)"
        />
      </slot>
    </div>
  </div>
</template>
<script>
import Glide from '@glidejs/glide';
import BaseBullets from '@shared/components/@atoms/BaseBullets/BaseBullets.vue';
import BaseButton from '@shared/components/@atoms/BaseButton/BaseButton.vue';
import BaseIcon from '@shared/components/@atoms/BaseIcon/BaseIcon.vue';

export default {
  name: 'BaseHero',
  components: {
    BaseIcon,
    BaseBullets,
    BaseButton,
  },
  props: {
    sliderOptions: {
      type: Object,
      default: () => ({}),
    },
    arrowIcon: {
      type: String,
      default: 'arrow',
    },
  },
  data() {
    return {
      glide: null,
      defaultOptions: {
        type: 'carousel',
        rewind: true,
        autoplay: 5000,
        perView: 1,
        gap: 0,
        disableButtonsAtBounds: false,
      },
    };
  },
  computed: {
    mergedOptions() {
      return {
        ...this.defaultOptions,
        ...this.sliderOptions,
      };
    },
    numberOfPages() {
      return this.$slots.default ? this.$slots.default.filter((slot) => slot.tag).length : 0;
    },
    page() {
      if (this.glide) {
        return this.glide.index + 1;
      }
      return 1;
    },
    disablePrevButton() {
      return this.mergedOptions.disableButtonsAtBounds && this.page === 1;
    },
    disableNextButton() {
      return this.mergedOptions.disableButtonsAtBounds && this.page === this.numberOfPages;
    },
  },
  mounted() {
    this.initializeGlide();
  },
  methods: {
    initializeGlide() {
      if (this.numberOfPages <= 1 || !this.$slots.default || !this.$refs.glide) {
        this.destroyGlide();
        return;
      }

      this.destroyGlide();
      try {
        const glide = new Glide(this.$refs.glide, this.mergedOptions);
        glide.mount();
        this.glide = glide;
      } catch (error) {
        console.error('Failed to initialize Glide:', error);
      }
    },
    destroyGlide() {
      if (this.glide) {
        this.glide.destroy();
        this.glide = null;
      }
    },
    go(direct) {
      if (!this.glide) return;
      switch (direct) {
        case 'prev':
          this.glide.go('<');
          break;
        case 'next':
          this.glide.go('>');
          break;
        default:
          this.glide.go(`=${direct}`);
          break;
      }
    },
  },
};
</script>
<style lang="scss">
@import '@glidejs/glide/dist/css/glide.core';
.sf-hero-item {
  display: flex;
  justify-content: flex-start;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  color: var(--c-text);
  background-repeat: no-repeat;
  background-size: cover;

  img,
  &__image {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    min-height: 14rem;
    padding: var(--spacer-sm);
    text-decoration: none;
    height: var(--hero-item-wrapper-height-mobile);

    &.sf-button,
    &.sf-button:hover,
    &.sf-button:active,
    &.sf-link {
      --button-background: transparent;
      --button-box-shadow-opacity: 0;
      --button-wrap: normal;
      text-align: left;
    }
  }
  &__subtitle {
    margin: 0;
    color: var(--c-gray);
    width: 60%;
    @include font(
      --hero-item-subtitle-font,
      var(--font-weight--normal),
      var(--h6-font-size),
      1.4,
      var(--font-family--secondary)
    );
    text-transform: uppercase;
  }
  &__title {
    margin: var(--spacer-xs) 0 0 0;
    color: var(--c-text);
    width: 60%;
    @include font(
      --hero-item-title-font,
      var(--font-weight--semibold),
      var(--h2-font-size),
      1.2,
      var(--font-family--secondary)
    );
    letter-spacing: 0.1px;
    text-transform: none;
  }
  &__button {
    display: none;
  }
  &--align-right {
    justify-content: flex-end;
  }
  &--position-bg-top-right {
    background-position: top right;
  }
  &--position-bg-bottom-right {
    background-position: bottom right;
  }
  &--position-bg-bottom-left {
    background-position: bottom left;
  }
  @include for-desktop {
    &__wrapper {
      width: 50%;
      min-height: 36.625rem;
      padding: var(--spacer-2xl);
    }
    &__subtitle {
      width: 100%;
    }
    &__title {
      --hero-item-font-size: var(--h1-font-size);
      width: 100%;
      margin: var(--spacer-base) 0 var(--spacer-lg) 0;
    }
    &__button {
      display: block;
    }
  }
}
.sf-hero {
  position: relative;
  &__control--left,
  &__control--right {
    position: absolute;
    top: 50%;
    transform: translate3d(0, -50%, 0);
    display: none;
    padding: 0 var(--spacer-sm);
  }
  &__control--right {
    right: 0;
  }
  &__bullets {
    position: absolute;
    bottom: var(--spacer-xl);
    left: 50%;
    transform: translate3d(-50%, 0, 0);
    display: none;
  }
  .glide {
    &__slides {
      margin: 0;

      > div {
        position: relative;
      }
    }
  }
  @include for-desktop {
    &__control--left,
    &__control--right {
      display: block;
    }
    &__bullets {
      display: flex;
    }
  }

  .sf-button {
    &.is-disabled--button {
      --button-background: transparent;
      opacity: 0.25;
    }
  }
}
</style>
